import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  OnInit,
  output,
} from '@angular/core';
import { NgOptimizedImage } from '@angular/common';
import { Observable, tap } from 'rxjs';
import { Dialog } from 'primeng/dialog';
import { Button } from 'primeng/button';
import {
  ImageThumbnailComponent,
  ImageThumbnailUnsupportedComponent,
  NoDataComponent,
  PrimengButtonDirective,
  TableBuilderFactory,
  TableComponent,
  TableConfig,
  TableDataPayload,
  TableLazyLoad,
  TableRowsSelectionChangeEvent,
} from '@web/shared/ui';
import {
  AdminCosmeticCollectionFilter,
  AdminCosmeticCollectionItem,
  AdminCosmeticDataSourceService,
} from '@web/shared/module/cosmetic';
import { Guid, isNotNil } from '@web/utils';

@Component({
  selector: 'app-admin-box-sale-cosmetic-select-modal',
  imports: [
    Button,
    Dialog,
    ImageThumbnailComponent,
    ImageThumbnailUnsupportedComponent,
    NgOptimizedImage,
    NoDataComponent,
    PrimengButtonDirective,
    TableComponent,
  ],
  templateUrl: './admin-box-sale-cosmetic-select-modal.component.html',
  styleUrl: './admin-box-sale-cosmetic-select-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminBoxSaleCosmeticSelectModalComponent implements OnInit {
  /* prettier-ignore */
  private readonly builder = inject(TableBuilderFactory<AdminCosmeticCollectionItem, AdminCosmeticCollectionFilter, never, never>).create();
  /* prettier-ignore */
  private readonly dataSource = inject(AdminCosmeticDataSourceService);

  public readonly excludedIds = input<Guid[]>([]);
  public readonly visible = input<boolean>(false);
  public readonly cancelEvent = output<void>();
  public readonly submitEvent = output<AdminCosmeticCollectionItem[]>();

  protected config!: TableConfig<
    AdminCosmeticCollectionItem,
    AdminCosmeticCollectionFilter,
    never,
    never
  >;

  protected readonly isNotNil = isNotNil;
  protected readonly loadedItems = new Map<Guid, AdminCosmeticCollectionItem>();
  protected selectedIds: Guid[] = [];
  protected readonly getColumnKey = <
    Key extends keyof AdminCosmeticCollectionItem,
  >(
    key: Key,
  ): Key => key;

  public ngOnInit(): void {
    this.buildTable();
  }

  protected handleVisibleChange(visible: boolean): void {
    if (!visible) {
      this.cancelEvent.emit();
    }
  }

  protected handleSelectionChangeEvent(
    event: TableRowsSelectionChangeEvent<AdminCosmeticCollectionItem>,
  ): void {
    this.selectedIds = event.includedIds as Guid[];
  }

  protected handleSubmitClick(): void {
    const selectedItems = this.selectedIds
      .map((id) => this.loadedItems.get(id))
      .filter(isNotNil);

    this.submitEvent.emit(selectedItems);
  }

  private buildTable(): void {
    this.config = this.builder
      .dataKey('id')
      .bodyRowSelectionDisabledFn((item) =>
        this.excludedIds().includes(item.id),
      )
      .preserveSelectionOnFilter(true)
      .scrollHeight('flex')
      .filters((builder) => [
        builder.initText('name').build(),
        builder.initText('cosmeticBrandName').build(),
      ])
      .columns((builder) => [
        builder.initSelection().build(),
        builder
          .initCustom('featuredFileUrl')
          .width('6rem')
          .label('Zdjęcie')
          .build(),
        builder
          .initText('name')
          .width('50rem')
          .label('Nazwa')
          .sortable(true)
          .sortKey('name')
          .filterable(true)
          .filterKey('name')
          .build(),
        builder
          .initText('cosmeticBrandName')
          .label('Marka')
          .sortable(true)
          .sortKey('cosmeticBrandName')
          .filterable(true)
          .filterKey('cosmeticBrandName')
          .build(),
      ])
      .dataSource({
        fetch: (
          lazyLoad: TableLazyLoad<AdminCosmeticCollectionFilter>,
        ): Observable<TableDataPayload<AdminCosmeticCollectionItem>> =>
          this.dataSource
            .fetchCollection(lazyLoad)
            .pipe(
              tap((payload) =>
                payload.data.forEach((item) =>
                  this.loadedItems.set(item.id, item),
                ),
              ),
            ),
      })
      .build();
  }
}
